import React, { useState, useEffect } from 'react';
import { Badge, Banner, BlockStack, Button, Card, DataTable, InlineStack, Spinner, Text } from '@shopify/polaris';
import { api } from './apiClient';
import { StoreProvider } from './StoreContext';
import StoresSection from './StoresSection';
import './AmazonPricingHub.css';

// The Pricing Hub page of the Hub's Settings (/settings/pricing-hub):
// the Amazon stores with their sync buttons, and the Repricer pull. Store edits
// are admin-only (StoresSection). Wrapped in its own StoreProvider because the
// Settings page sits outside the tool's routes.
export default function PricingHubSettings() {
    return (
        <StoreProvider>
            <PricingHubSettingsBody />
        </StoreProvider>
    );
}

function PricingHubSettingsBody() {
    const [repSyncing, setRepSyncing] = useState(false);
    const [repProgress, setRepProgress] = useState(null);
    const [repLastSync, setRepLastSync] = useState(null);
    const [repError, setRepError] = useState('');

    useEffect(() => {
        // Last repricer sync time
        api.summary('all', 'all').then(d => { if (d) setRepLastSync(d.last_repricer_sync); }).catch(() => {});
        // Is a repricer sync already running?
        api.repricerProgress().then(p => {
            if (p && p.running) {
                setRepSyncing(true);
                setRepProgress(p);
            }
        }).catch(() => {});
    }, []);

    // Poll progress while a repricer sync runs
    useEffect(() => {
        if (!repSyncing) return;
        let stopped = false;
        const poll = setInterval(async () => {
            if (stopped) return;
            try {
                const p = await api.repricerProgress();
                setRepProgress(p);
                if (!p.running && p.phase) {
                    setTimeout(() => { if (!stopped) { setRepSyncing(false); } }, 3000);
                    clearInterval(poll);
                    api.summary('all', 'all').then(d => { if (d && !stopped) setRepLastSync(d.last_repricer_sync); }).catch(() => {});
                }
            } catch (e) { /* transient */ }
        }, 3000);
        return () => { stopped = true; clearInterval(poll); };
    }, [repSyncing]);

    const runRepricerSync = async () => {
        if (repSyncing) return;
        setRepError('');
        try {
            await api.startSync('repricer', 'all');
        } catch (e) {
            setRepError(e.message || 'Failed to start the Repricer sync');
            return;
        }
        setRepSyncing(true);
        setRepProgress({ running: true, phase: 'Starting...', detail: '' });
    };

    return (
        <BlockStack gap="400">
            <StoresSection />
            <CredentialsCard />
            <Card>
                <BlockStack gap="300">
                    <BlockStack gap="050">
                        <Text as="h3" variant="headingMd">Repricer data</Text>
                        <Text as="p" tone="subdued">Pulls every store's min, max, rule and cost from Repricer.com. One report covers all stores.</Text>
                    </BlockStack>
                    <InlineStack gap="300" blockAlign="center">
                        <Button variant="primary" onClick={runRepricerSync} disabled={repSyncing} loading={repSyncing}>Sync Repricer</Button>
                        <Text as="span" tone="subdued">Last pull: {repLastSync ? new Date(repLastSync).toLocaleString() : 'Never'}</Text>
                    </InlineStack>
                    {repError && <Banner tone="critical">{repError}</Banner>}
                    {repSyncing && repProgress && (
                        <InlineStack gap="200" blockAlign="center">
                            <Spinner size="small" accessibilityLabel="Repricer sync running" />
                            <Text as="span" fontWeight="semibold">{repProgress.phase || 'Starting...'}</Text>
                            {repProgress.detail && <Text as="span" tone="subdued">{repProgress.detail}</Text>}
                        </InlineStack>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

const STATE_TONE = { ok: 'success', warn: 'attention', expired: 'critical', unknown: 'warning' };
const STATE_LABEL = { ok: 'OK', warn: 'Rotate soon', expired: 'Expired', unknown: 'Date not set' };
const fmtDate = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const daysText = (d) => (d == null ? '—' : d < 0 ? `${-d} days ago` : d === 0 ? 'today' : `${d} days`);

// Amazon expires each SP-API app's client secret 180 days after it was rotated; when it goes, every
// store on that app stops syncing (2026-10-24). This card counts down per app from the rotation date
// (shared app: SP_API_SECRET_ROTATED_AT in .env; a store's own app: its "secret rotated on" field).
function CredentialsCard() {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    useEffect(() => {
        api.credentials().then(setData).catch(e => setError(e.message || 'Could not load credential status'));
    }, []);

    if (error) return <Card><Banner tone="critical">{error}</Banner></Card>;
    if (!data) return <Card><InlineStack gap="200" blockAlign="center"><Spinner size="small" accessibilityLabel="Loading credential status" /><Text as="span" tone="subdued">Checking SP-API credentials…</Text></InlineStack></Card>;

    const shared = data.shared;
    const ownApps = data.stores.filter(s => s.app === 'own');
    const rows = [
        [`Shared app …${shared.client_id_suffix || '?'}`, shared.stores.join(', ') || '—', fmtDate(shared.rotated_at), fmtDate(shared.expires_at), daysText(shared.days_left),
            <Badge key="s" tone={STATE_TONE[shared.state]}>{STATE_LABEL[shared.state]}</Badge>],
        ...ownApps.map(s => [`Own app …${s.client_id_suffix || '?'}`, s.display_name, fmtDate(s.rotated_at), fmtDate(s.expires_at), daysText(s.days_left),
            <Badge key={s.id} tone={STATE_TONE[s.state]}>{STATE_LABEL[s.state]}</Badge>]),
    ];
    const banner = data.worst === 'expired'
        ? <Banner tone="critical" title="An SP-API client secret has expired">Every store on that app answers 403 until it is rotated in Developer Central and updated here.</Banner>
        : data.worst === 'warn'
            ? <Banner tone="warning" title={`An SP-API client secret expires within ${data.warn_days} days`}>Rotate it in Developer Central (Soccer Post Pricing Portal › LWA credentials), put the new value in .env, restart the Hub, and record the date.</Banner>
            : data.worst === 'unknown'
                ? <Banner tone="info">A rotation date is missing, so that app's expiry can't be shown. Shared app: set SP_API_SECRET_ROTATED_AT in .env; a store's own app: Edit the store and fill "Secret rotated on".</Banner>
                : null;

    return (
        <Card>
            <BlockStack gap="300">
                <BlockStack gap="050">
                    <Text as="h3" variant="headingMd">SP-API credentials</Text>
                    <Text as="p" tone="subdued">Amazon expires each app's client secret {data.lifetime_days} days after it is rotated. Refresh tokens do not expire with it.</Text>
                </BlockStack>
                {banner}
                <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                    headings={['App', 'Stores', 'Secret rotated', 'Expires', 'Time left', 'Status']}
                    rows={rows}
                    increasedTableDensity
                />
            </BlockStack>
        </Card>
    );
}
